/**
 * Builders for the TV's full summary layout (tv/summary.ts TvSummarySection).
 * Each summary screen hands in the values it ALREADY computed for its own
 * phone UI (display strings and resolved colors), and these functions only
 * arrange them into sections and columns. Pure: no React Native, no theme
 * import, so they are unit-tested directly.
 */
import type {
  TvSummary,
  TvSummaryHouseSection,
  TvSummaryPuttsSection,
  TvSummarySection,
  TvSummaryStartLinesSection,
  TvSummaryText,
  TvSummaryTextSize,
} from './summary';

/** One line of text for a text section. */
export function tvText(
  text: string,
  color?: string,
  size: TvSummaryTextSize = 'body',
  weight: TvSummaryText['weight'] = 'regular',
  spacing?: number,
): TvSummaryText {
  return {
    text,
    ...(color ? { color } : {}),
    size,
    weight,
    ...(spacing != null ? { spacing } : {}),
  };
}

/** A label/value row ("THIS ROUND ... #4"). */
export function tvRow(
  label: TvSummaryText,
  value: string,
  valueColor?: string,
  valueSize: TvSummaryTextSize = 'body',
  valueWeight: TvSummaryText['weight'] = 'heavy',
): TvSummaryText {
  return {
    ...label,
    rightText: value,
    ...(valueColor ? { rightColor: valueColor } : {}),
    rightSize: valueSize,
    rightWeight: valueWeight,
  };
}

export const TV_DIVIDER: TvSummaryText = { text: '', divider: true };

/** Only these fields of a summary carry the full layout. */
export type TvSummaryLayout = Pick<TvSummary, 'sections' | 'columns'>;

/* ---- Distance Challenge summary (components/ChallengeSummary.tsx) ---- */

export interface ChallengeSummaryTvInput {
  /** HeroPage's proximity block, as the phone renders it. */
  hero: TvSummaryText[];
  /** The rank badge or the daily's rank lines (null = none shown). */
  supplement: TvSummaryText[] | null;
  /** "PUTT BY PUTT" card. */
  putts: Pick<TvSummaryPuttsSection, 'header' | 'rows'>;
  /** "WHERE THEY LANDED". */
  house: Omit<TvSummaryHouseSection, 'kind' | 'column' | 'title'>;
  /** "START LINES". */
  startLines: Omit<TvSummaryStartLinesSection, 'kind' | 'column' | 'title'>;
}

/**
 * The phone's pager pages (summary, round detail, where they landed, start
 * lines) side by side: the hero card over the putt list, the house plot, and
 * the start-line chart.
 */
export function challengeSummaryLayout(input: ChallengeSummaryTvInput): TvSummaryLayout {
  const heroLines = input.supplement?.length
    ? [...input.hero, TV_DIVIDER, ...input.supplement]
    : input.hero;
  const sections: TvSummarySection[] = [
    { kind: 'text', column: 0, card: true, align: 'center', lines: heroLines },
    { kind: 'putts', column: 0, card: true, ...input.putts },
    { kind: 'house', column: 1, title: 'WHERE THEY LANDED', ...input.house },
    { kind: 'startLines', column: 2, title: 'START LINES', ...input.startLines },
  ];
  return { sections, columns: [1, 1.15, 1] };
}

/* ---- House plot balls ---- */

/** Balls for a house section: each ball's color as the phone draws it and the
 *  winner's-best halo (summaryVisuals/curlingHouse isWinnersBest). */
export function tvHouseBalls(
  balls: ReadonlyArray<{
    side: 'challenger' | 'opponent';
    order: number;
    longitudinalMissFt: number;
    lateralMissFt: number;
    color?: string;
  }>,
  sideColors: { challenger: string; opponent: string },
  winnerHaloSide: 'challenger' | 'opponent' | null,
  isWinnersBest: (ball: (typeof balls)[number], all: typeof balls) => boolean,
): TvSummaryHouseSection['balls'] {
  return balls.map((ball) => ({
    side: ball.side,
    order: ball.order,
    longitudinalMissFt: ball.longitudinalMissFt,
    lateralMissFt: ball.lateralMissFt,
    color: ball.color ?? sideColors[ball.side],
    ...(winnerHaloSide === ball.side && isWinnersBest(ball, balls) ? { halo: true } : {}),
  }));
}

/* ---- Friend distance match result (screens/friendChallenges/ChallengeResultScreen.tsx) ---- */

export interface ChallengeResultTvInput {
  /** Result word and distance lines. */
  header: TvSummaryText[];
  versus: Omit<import('./summary').TvSummaryVersusSection, 'kind' | 'column'>;
  /** null when the server has no shot data (the phone shows the notice). */
  house: Omit<TvSummaryHouseSection, 'kind' | 'column' | 'title'> | null;
  /** The notice or legend line under the plot. */
  plotNote: TvSummaryText[];
  /** One chart per player ("START LINE · @handle"); null angles = unavailable line. */
  startLines: Array<{
    title: string;
    chart: Omit<TvSummaryStartLinesSection, 'kind' | 'column' | 'title'> | null;
    unavailable: TvSummaryText[];
  }>;
  footer: TvSummaryText[];
  /** Where your round would rank on the Distance board (LeaderboardRankBadge's
   *  TV lines; Cole, 2026-10-02). Null while loading or for a guest. */
  rank?: TvSummaryText[] | null;
}

export function challengeResultLayout(input: ChallengeResultTvInput): TvSummaryLayout {
  const sections: TvSummarySection[] = [
    { kind: 'text', column: 0, align: 'center', lines: input.header },
    { kind: 'versus', column: 0, ...input.versus },
    ...(input.rank?.length ? [{ kind: 'text' as const, column: 0, align: 'center' as const, card: true, lines: input.rank }] : []),
    { kind: 'text', column: 0, align: 'center', lines: input.footer },
  ];
  if (input.house) {
    sections.push({ kind: 'house', column: 1, card: true, ...input.house, caption: input.plotNote });
  } else {
    sections.push({ kind: 'text', column: 1, align: 'center', lines: input.plotNote });
  }
  for (const chart of input.startLines) {
    sections.push(
      chart.chart
        ? { kind: 'startLines', column: 2, title: chart.title, ...chart.chart }
        : { kind: 'text', column: 2, title: chart.title, align: 'center', lines: chart.unavailable },
    );
  }
  return { sections, columns: [1, 1.1, 1] };
}

/* ---- Text-only summaries ---- */

/** A text section (optionally a card). */
export function tvTextSection(
  column: number,
  lines: TvSummaryText[],
  options: { card?: boolean; border?: string | null; title?: string; align?: 'center' | 'left' } = {},
): TvSummarySection {
  return {
    kind: 'text',
    column,
    lines,
    align: options.align ?? 'center',
    ...(options.card ? { card: true } : {}),
    ...(options.border ? { border: options.border } : {}),
    ...(options.title ? { title: options.title } : {}),
  };
}

/** Sections plus column widths; columns default to one per used index. */
export function tvLayout(sections: TvSummarySection[], columns?: number[]): TvSummaryLayout {
  const used = sections.reduce((max, section) => Math.max(max, section.column ?? 0), 0);
  return { sections, columns: columns ?? Array.from({ length: used + 1 }, () => 1) };
}
